import { TableSortLabel } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { SORT_ORDER_ASC, SORT_ORDER_DESC } from "core/consts";
import { isPrintMode } from "core/model/utils/urls";
import { SortOrder, TourElement } from "core/types";
import { SpinnerPage } from "ds_legacy/components/Spinner";
import { ToggleTip } from "ds_legacy/components/ToggleTip";
import Tooltip from "ds_legacy/components/Tooltip";
import {
  BORDER_BOTTOM,
  ICON_DARK,
  PRIMARY_COLOR,
} from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import {
  border,
  dp,
  margin,
  padding,
  sizing,
} from "ds_legacy/materials/metrics";
import { FONT_SIZE_12, FONT_SIZE_16 } from "ds_legacy/materials/typography";
import useHasOverflown from "dsl/hooks/useHasOverflown";
import { InfoIcon } from "lucide-react";
import React, {
  CSSProperties,
  DependencyList,
  MouseEvent,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import styled from "styled-components";
import { useLocale } from "translations";
import Translations from "translations/types";
import { COLUMN_TYPE_DEFAULT_ACTION } from "./columnTypeConstants";
import { ColumnKinds, GeneralTableColumn } from "./CustomCells";
import { ActionMenuOption, GeneralListActionKind } from "./MenuActions";
import {
  StyledGeneralTable,
  TableIntroSubtitle,
  TableIntroTitle,
} from "./styles";
import { convertAlignToJustifyContent, useGetTableBody } from "./utils";

export type GeneralTableActions<
  Data extends { id: number | string },
  RowActions extends GeneralListActionKind[],
> = {
  allowList?: (data: Data) => RowActions;
  getOptions: (props: {
    data: Data;
    translations: Translations;
  }) => ActionMenuOption<RowActions>[] | (() => ActionMenuOption<RowActions>[]);
  getTestId?: (data: Data) => string;
  onActionMenuClick?: (e: MouseEvent<HTMLButtonElement>, data: Data) => void;
  renderCustomAction?: (data: Data) => ReactNode;
  tour?: (rowNumber: number) => TourElement | undefined;
  width?: string;
};

export type GeneralTableProps<
  Data extends { id: number | string },
  RowActions extends GeneralListActionKind[],
> = {
  actions?: GeneralTableActions<Data, RowActions>;
  columns: GeneralTableColumn<NonNullable<Data>, ColumnKinds>[];
  data: readonly Data[];
  dependencies?: DependencyList;
  emptyComponent: ReactNode | null;
  getA11yRowProps?: (props: { data: Data; index: number }) => AnyObject;
  getRowStyle?: (props: { data: Data; index: number }) => CSSProperties;
  id: string;
  isLoading?: boolean;
  isMessenger?: boolean;
  isSelected?: (id: Data["id"]) => boolean;
  onRowClick?: (props: {
    event: MouseEvent<HTMLElement>;
    rowData: Data;
  }) => void;
  rowClickDisabled?: (props: { data: Data; index: number }) => boolean;
  setOrder?: (sortOrder: SortOrder) => void;
  setOrderBy?: (sortBy: string) => void;
  sortBy?: string;
  sortOrder?: SortOrder;
  tour?: TourElement;
};

function Loading({ delay = 350, id }: { delay?: number; id: string }) {
  return (
    <HorizontalLayout
      aligned
      justify="center"
      margin="0 auto"
      maxWidth="100%"
      minHeight={dp(150)}
      padding={padding(4, 0)}
    >
      <SpinnerPage delay={delay} id={`${id}-spinner`} />
    </HorizontalLayout>
  );
}

type StyledInnerCellWrapperProps = {
  disableDivider?: boolean;
  isTransparentDivider?: boolean;
};

export const StyledInnerCellWrapper = styled(HorizontalLayout).withConfig({
  shouldForwardProp: (prop) =>
    prop !== "disableDivider" && prop !== "isTransparentDivider",
})<StyledInnerCellWrapperProps>`
  overflow: visible;
  position: relative;
  width: 100%;

  ${({ disableDivider, isTransparentDivider }) =>
    disableDivider
      ? ""
      : `
  padding-left: ${dp(14)};
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    height: ${dp(14)};
    border-left: ${border({
      color: isTransparentDivider ? "transparent" : BORDER_BOTTOM,
      width: 2,
    })};
    width: ${dp(14)};
    `}
`;

export function TableIntro({
  icon,
  leftAction,
  rightAction,
  subtitle,
  testId,
  title,
  tour,
}: {
  icon?: React.JSX.Element;
  leftAction?: React.JSX.Element;
  rightAction?: React.JSX.Element;
  subtitle?: string;
  testId?: string;
  title?: string;
  tour?: TourElement;
}) {
  const isPrint = isPrintMode();
  if (!icon && !subtitle && !title) return null;

  if (isPrint)
    return (
      <VerticalLayout width="100%">
        {title ? (
          <TableIntroTitle
            style={{ fontSize: FONT_SIZE_12, padding: padding(0, 1) }}
          >
            {title}
          </TableIntroTitle>
        ) : null}
        {subtitle ? <TableIntroSubtitle>{subtitle}</TableIntroSubtitle> : null}
      </VerticalLayout>
    );

  return (
    <VerticalLayout
      data-testid={testId ?? "table-intro"}
      gap={dp(5)}
      padding={padding(2, 0)}
    >
      <HorizontalLayout aligned gap={dp(6)} overflow="visible">
        {icon ? (
          <VerticalLayout
            aligned
            style={{
              color: PRIMARY_COLOR,
              width: dp(30),
            }}
          >
            {icon}
          </VerticalLayout>
        ) : null}
        {title ? <TableIntroTitle {...tour}>{title}</TableIntroTitle> : null}
        {leftAction ?? null}
        <div
          style={{
            marginLeft: "auto",
            marginRight: sizing(1),
          }}
        >
          {rightAction ?? null}
        </div>
      </HorizontalLayout>
      {subtitle ? (
        <TableIntroSubtitle icon={!!icon}>{subtitle}</TableIntroSubtitle>
      ) : null}
    </VerticalLayout>
  );
}

function ColumnHeaderTitle({
  align,
  title,
}: {
  align: GeneralTableColumn<never, ColumnKinds>["align"];
  title: string;
}) {
  const { hasOverflown, ref } = useHasOverflown<HTMLSpanElement>({
    direction: "x",
    value: title,
  });

  return (
    <Tooltip title={hasOverflown ? title : ""}>
      <span
        ref={ref}
        style={{
          display: "-webkit-box",
          flex: "1 1 0%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          overflowWrap: "normal",
          textAlign: align,
          textOverflow: "ellipsis",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          wordBreak: "normal",
        }}
      >
        {title}
      </span>
    </Tooltip>
  );
}

function EnhancedTableHead<
  Data extends { id: number | string },
  RowActions extends GeneralListActionKind[],
>({
  actions,
  onRequestSort,
  order,
  orderBy,
  visibleColumns,
}: {
  actions?: GeneralTableActions<Data, RowActions>;
  onRequestSort: (property: string) => void;
  order?: SortOrder;
  orderBy?: string;
  visibleColumns: GeneralTableColumn<NonNullable<Data>, ColumnKinds>[];
}) {
  const isPrint = isPrintMode();

  const renderColumn = useCallback(
    (
      props: GeneralTableColumn<NonNullable<Data>, ColumnKinds>,
      index: number,
    ) => {
      const { align, disableDivider, hint, sortBy, title, tour, type, width } =
        props;
      const isFirstColumn = index === 0;
      const shouldDisableDivider = isFirstColumn || disableDivider;

      return (
        <TableCell
          key={index}
          align={align}
          data-type={type}
          sx={{
            overflowWrap: "normal",
            whiteSpace: "normal",
            width,
            wordBreak: "normal",
          }}
        >
          <StyledInnerCellWrapper
            disableDivider={shouldDisableDivider}
            {...tour}
            style={{
              flex: 1,
              justifyContent: convertAlignToJustifyContent(align),
              minWidth: 0,
              textAlign: align,
            }}
          >
            {sortBy ? (
              <TableSortLabel
                active={orderBy === sortBy}
                direction={orderBy === sortBy ? order : SORT_ORDER_ASC}
                sx={{
                  "& .MuiTableSortLabel-icon": {
                    marginRight: align === "right" ? undefined : 0,
                  },
                  flex: "1 1 0%",
                  flexDirection: align === "right" ? "row-reverse" : undefined,
                  maxWidth: "100%",
                  minWidth: 0,
                  textAlign: align,
                }}
                onClick={() => onRequestSort(sortBy)}
              >
                <span
                  style={{
                    display: "-webkit-box",
                    maxWidth: "100%",
                    minWidth: 0,
                    overflow: "hidden",
                    overflowWrap: "normal",
                    textAlign: align,
                    textOverflow: "ellipsis",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    wordBreak: "normal",
                  }}
                >
                  {title}
                </span>
              </TableSortLabel>
            ) : typeof title === "string" ? (
              <ColumnHeaderTitle align={align} title={title} />
            ) : (
              <span
                style={{
                  display: "-webkit-box",
                  flex: "1 1 0%",
                  maxWidth: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                  overflowWrap: "normal",
                  textAlign: align,
                  textOverflow: "ellipsis",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  wordBreak: "normal",
                }}
              >
                {title}
              </span>
            )}
            {hint && !isPrint && (
              <HorizontalLayout aligned flexShrink={0}>
                <ToggleTip
                  ariaLabel={hint?.toString()}
                  content={hint}
                  label={
                    <InfoIcon
                      size={FONT_SIZE_16}
                      style={{
                        color: ICON_DARK,
                        fontSize: FONT_SIZE_16,
                        margin: margin(0, 0, 0, 1),
                      }}
                    />
                  }
                  testId="table-column-hint"
                  toggleTipWrapperStyle={{
                    display: "contents",
                  }}
                />
              </HorizontalLayout>
            )}
          </StyledInnerCellWrapper>
        </TableCell>
      );
    },
    [order, orderBy, onRequestSort, isPrint],
  );

  const columns = useMemo(
    () => visibleColumns.map(renderColumn),
    [visibleColumns, renderColumn],
  );

  return (
    <TableHead>
      <TableRow>
        {columns}
        {actions ? (
          <TableCell
            data-type={COLUMN_TYPE_DEFAULT_ACTION}
            sx={{ width: actions.width ?? sizing(4) }}
          />
        ) : null}
      </TableRow>
    </TableHead>
  );
}

export default function GeneralTable<
  Data extends { id: number | string },
  RowActions extends GeneralListActionKind[] = [],
>({
  actions,
  columns,
  data,
  dependencies,
  emptyComponent,
  getA11yRowProps,
  getRowStyle,
  id,
  isLoading,
  isSelected,
  onRowClick,
  rowClickDisabled,
  setOrder,
  setOrderBy,
  sortBy,
  sortOrder,
  tour,
}: GeneralTableProps<Data, RowActions>) {
  const locale = useLocale();
  const { tableBody, visibleColumns } = useGetTableBody<Data, RowActions>({
    actions,
    columns,
    data,
    dependencies: [locale, ...(dependencies ?? [])],
    getA11yRowProps,
    getRowStyle,
    id,
    isSelected,
    onRowClick,
    rowClickDisabled,
    tour,
  });

  const handleRequestSort = useCallback(
    (property: string) => {
      const isAsc = sortBy === property && sortOrder === SORT_ORDER_ASC;
      setOrder?.(isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC);
      setOrderBy?.(property);
    },
    [sortBy, sortOrder, setOrder, setOrderBy],
  );

  const memoizedTableHead = useMemo(
    () => (
      <EnhancedTableHead<Data, RowActions>
        actions={actions}
        order={sortOrder}
        orderBy={sortBy}
        visibleColumns={visibleColumns}
        onRequestSort={handleRequestSort}
      />
    ),
    [actions, visibleColumns, sortOrder, sortBy, handleRequestSort],
  );

  if (!data?.length && !isLoading) {
    return <>{emptyComponent}</>;
  }

  if (isLoading || !tableBody?.length) {
    return <Loading id={id} />;
  }

  return (
    <StyledGeneralTable data-testid={id}>
      {memoizedTableHead}
      <TableBody>{tableBody}</TableBody>
    </StyledGeneralTable>
  );
}
